<script lang="ts">
  import { gsap, Power1 } from "gsap";
  import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
  import { onDestroy, onMount } from "svelte";
  import type { TiltedGalleryItemStoryblok } from "$types/components";
  import { RGB_Log_Shade } from "../utils/helpers";

  export let blok: TiltedGalleryItemStoryblok;
  export let index = 0;
  export let backgroundColor = "";
  export let cardsShown = 3;
  export let mode: "normal" | "fullscreen" | "testimonials-with-logo" =
    "normal";
  export let activeBackgroundColor: string;

  let container: HTMLElement;
  let mask: HTMLElement;
  let inner: HTMLElement;
  let activeColor: string;
  const defaultColor = "--color-warm-grey";
  let shadeMedium = "";
  let shadeLight = "";
  let blur = 0;
  let scaleNonActive = 0;
  const activeRotationInAngle = mode === "fullscreen" ? 3 : 7;
  const ease = Power1.easeOut;
  let duration: number;
  let delay: number;
  let scroller: ScrollTrigger;

  const fluid = (value: number, min: number | null = null) => {
    if (window.innerWidth >= 1440) {
      return value;
    }
    if (min) {
      return Math.max((value * window.innerWidth) / 1440, min);
    } else {
      return (value * window.innerWidth) / 1440;
    }
  };

  function animateMask(
    backgroundColor: string,
    rotationX: number,
    y: string | number,
    z: number,
    scale: number,
    opacity: number,
  ) {
    const options = {
      backgroundColor,
      rotationX,
      y,
      z,
      opacity,
      scale,
      duration,
      delay,
      ease,
    };

    // if (mode === 'testimonials-with-logo') {
    //   if (scale === 0.9) {
    //     options.opacity = 0.66
    //   }
    //   if (scale === 0.8) {
    //     options.opacity = 0.33
    //   }
    // }

    gsap.to(mask, options);
  }

  function animateInner(
    filter: string,
    scale: number,
    rotationX: number,
    autoAlpha = 0,
  ) {
    gsap.to(inner, {
      autoAlpha,
      filter,
      scale,
      rotationX,
      duration,
      delay,
      ease,
    });
  }

  const setRotation = (animate: boolean = true) => {
    const opacityThirdCard = cardsShown === 2 ? 0 : 1;
    duration = animate ? 0.3 : 0;
    delay = animate && index > -1 ? 0.1 : 0;

    if (index < 0) {
      animateMask(
        activeColor,
        mode === "fullscreen" ? 30 : 30,
        fluid(-130),
        fluid(25),
        1,
        0,
      );
      animateInner("blur(0px)", 1, 0);
    } else if (index === 0) {
      animateMask(activeColor, 0, 0, 0, 1, 1);
      animateInner("blur(0px)", 1, 0, 1);
    } else if (index === 1) {
      animateMask(
        shadeLight,
        8,
        `${mode === "fullscreen" ? 12 : 15}%`,
        -fluid(50, 50),
        0.9,
        1,
      );
      animateInner(`blur(${blur}px)`, scaleNonActive, -10, 0);
    } else if (index === 2) {
      animateMask(
        shadeMedium,
        10,
        "32%",
        -fluid(90, 90),
        0.8,
        opacityThirdCard,
      );
      animateInner(`blur(${blur}px)`, scaleNonActive, -10, 0);
    } else if (index > 2) {
      animateMask(shadeMedium, 20, "35%", -fluid(180, 180), 0.7, 0);
      animateInner(`blur(${blur}px)`, scaleNonActive, -20, 0);
    }
  };

  // Exported function exposes a way to toggle 'marked'
  export function advance(direction: number) {
    index = index - direction;
    setRotation();
  }

  function setColorShades() {
    let activeColorArray: Array<string> = [];
    let backgroundRGBArray: Array<string> = [];

    if (mode === "testimonials-with-logo") {
      if (!backgroundColor || backgroundColor === "green") {
        activeColor = `rgb(4, 70, 47)`;
        shadeLight = `rgb(182, 198, 191)`;
        shadeMedium = `rgb(218, 224, 220)`;
      } else if (backgroundColor === "blue") {
        activeColor = `rgb(52, 94, 209)`;
        shadeLight = `rgb(194, 204, 231)`;
        shadeMedium = `rgb(223, 226, 236)`;
      } else if (backgroundColor === "green-newsroom") {
        activeColor = `rgb(242,241,239)`;
        shadeLight = `rgb(37 89 70)`;
        shadeMedium = `rgb(24 79 60)`;
      } else {
        activeColor = `rgb(255, 255, 255)`;
        shadeLight = `rgb(230,230,230)`;
        shadeMedium = `rgb(210,210,210)`;
      }
    } else {
      if (backgroundColor) {
        activeColorArray = getColorArray(
          document.documentElement,
          activeBackgroundColor || "--color-white",
        );
        const lightRgb = getColorArray(
          document.documentElement,
          `--color-${backgroundColor}-tint-light`,
        );
        const mediumRgb = getColorArray(
          document.documentElement,
          `--color-${backgroundColor}-tint-medium`,
        );

        activeColor = `rgb(${activeColorArray.join(",")})`;
        shadeMedium = `rgb(${mediumRgb.join(",")})`;
        shadeLight = `rgb(${lightRgb.join(",")})`;
      } else {
        activeColorArray = getColorArray(
          document.documentElement,
          defaultColor,
        );
        backgroundRGBArray = getColorArray(
          document.documentElement,
          defaultColor,
        );
        activeColor = `rgb(${activeColorArray.join(",")})`;
        shadeMedium = RGB_Log_Shade(
          -0.1,
          `rgb(${backgroundRGBArray.join(",")})`,
        );
        shadeLight = RGB_Log_Shade(
          -0.05,
          `rgb(${backgroundRGBArray.join(",")})`,
        );
      }
    }
  }

  function getColorArray(el: HTMLElement, color: string) {
    const array = getComputedStyle(el).getPropertyValue(color).split(" ");
    if (array.length > 3) array.shift();
    return array;
  }

  function setScrollRotation(progress: number) {
    if (index === 0) {
      gsap.to(mask, { rotationX: activeRotationInAngle * (1 - progress) });
    }
    // Serguei updates!!
    else if (index === 1) {
      gsap.to(mask, {
        y: `${(mode === "fullscreen" ? 12 : 15) * progress}%`,
        duration: 0.6,
      });
    } else if (index === 2) {
      gsap.to(mask, { y: `${32 * progress}%`, duration: 0.6 });
    }
  }

  function initScrollTrigger() {
    setScrollRotation(0);
    gsap.registerPlugin(ScrollTrigger);
    scroller = new ScrollTrigger({
      scroller: document.body,
      trigger: container,
      start: "bottom bottom",
      end: "bottom 90%",
      markers: false,
      scrub: 1,
      onEnter: (e) => setScrollRotation(e.progress),
      onUpdate: (e) => setScrollRotation(e.progress),
    });
  }

  onMount(() => {
    const componentName: string = blok.component;
    blur =
      componentName === "galleryImage" || componentName === "galleryVideo"
        ? 5
        : 0;
    scaleNonActive =
      componentName === "galleryImage" || componentName === "galleryVideo"
        ? 1.1
        : 1;
    gsap.set(container, { perspective: 2000 });
    gsap.set(mask, { perspective: 500 });
    setColorShades();
    setRotation(false);
    initScrollTrigger();
  });

  onDestroy(() => {
    scroller && scroller.kill();
  });

  const resize = () => {
    setRotation();
  };
</script>

<svelte:window on:resize={resize} />
<div
  class="tilted-gallery-item {index !== 0
    ? 'disabled'
    : 'active'} tilted-gallery-item{activeBackgroundColor} {mode}"
  bind:this={container}
  data-index={index}
>
  <div bind:this={mask} class="mask">
    <div bind:this={inner} class="inner">
      <slot name="slide" prop={blok} {index} />
    </div>
  </div>
</div>

<style lang="scss">
  :global(.section[class*="background-"]) .mask {
    background: rgb(var(--color-white));
  }

  .tilted-gallery-item {
    width: 100%;
    height: 100%;
    position: absolute;
    opacity: 1;
    color: rgb(var(--color-black));
    transform-style: preserve-3d;

    &.disabled {
      pointer-events: none;
    }
  }

  .mask {
    overflow: hidden;
    border-radius: 10px;
    width: 100%;
    transform-style: preserve-3d;
    background: rgb(var(--color-warm-grey));
    height: 100%;
  }

  .inner {
    transform-style: preserve-3d;
    position: relative;
    height: 100%;
  }
</style>
